<template>
  <div class="e-dialog e-dialog--new">
    <router-link :to="{ name: 'dialogs' }" class="e-dialog__back">
      <IconChevronLeft />
    </router-link>

    <div class="e-dialog__inner">
      <div class="e-dialog__scroll">
        <NewMessageHint v-if="showNewHint" />
        <MessageEditor
          :edit-message="editMessage"
          :save-status="saveStatus"
          @change="onEdited"
          @send="onSend"
          @cancel="onEditingCancel"
          @delete="onDeleteDraft"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'

import NewMessageHint from 'atoms/NewMessageHint/NewMessageHint'
import MessageEditor from 'organisms/MessageEditor/MessageEditor'
import Dialog from 'store/models/Dialog'
import DialogMessage from 'store/models/DialogMessage'

// Icons
import IconChevronLeft from 'assets/icons/chevron-left.svg'

export default {
  name: 'NewDialog',
  components: {
    IconChevronLeft,
    MessageEditor,
    NewMessageHint
  },
  data: () => ({
    saveStatus: 'unsaved',
    messageEditorOpen: false,
    initialEditMessage: {
      id: null,
      subject: '',
      body: ''
    },
    editMessage: {
      id: null,
      subject: '',
      body: ''
    },
    draftId: null
  }),
  computed: {
    ...mapGetters([
      'currentDialog',
      'dialogs',
      'userInfo',
      'userId',
      'kidMessageSentMessage'
    ]),
    showNewHint() {
      return false
      // return (
      //   !this.userInfo.disableHint &&
      //   this.dialogs.filter(el => !el.outgoing).length > 0
      // )
    },
    messages() {
      const dialogId = this.draftId ?? this.$route.params.dialogId ?? null
      if (dialogId === null) {
        return []
      }
      const dialog = Dialog.query()
        .with('messages', query => {
          query.orderBy('timestamp', 'desc')
        })
        .whereId(dialogId)
        .first()
      return dialog?.messages ?? []
    },
    hasUnsavedChanges() {
      return (
        this.initialEditMessage.body !== this.editMessage.body ||
        this.initialEditMessage.subject !== this.editMessage.subject
      )
    },

    onEdited() {
      return debounce(this.autoSaveEditMessage, 3000, {
        leading: false,
        trailing: true
      })
    }
  },
  watch: {
    messages: {
      handler() {
        let message =
          this.messages.length > 0 ? this.messages[0] : new DialogMessage()
        this.setEditMessage(message)
      },
      immediate: true
    },
    saveStatus(status) {
      if (['autoSaved', 'sending'].includes(status)) {
        setTimeout(() => {
          this.saveStatus = 'unsaved'
        }, 2000)
      }
    }
  },
  methods: {
    setEditMessage(message) {
      const emptyMessage = new DialogMessage()
      this.initialEditMessage = Object.assign({}, emptyMessage, message)
      this.editMessage = Object.assign({}, emptyMessage, message)
    },
    async autoSaveEditMessage() {
      if (this.saveStatus === 'autoSaving' || this.saveStatus === 'sending') {
        return
      }
      this.saveStatus = 'autoSaving'
      const dialogId = await Dialog.autoSave(this.userId, this.editMessage)
      if (dialogId && this.draftId === null) {
        this.draftId = dialogId
      }
      this.saveStatus = 'autoSaved'
    },

    async onSend() {
      this.onEdited.cancel()
      let oldStatus = this.saveStatus
      this.saveStatus = 'sending'
      try {
        const dialogId = await DialogMessage.send(this.userId, this.editMessage)
        this.setEditMessage()
        this.messageEditorOpen = false
        this.$successMessage(this.kidMessageSentMessage, { duration: 30000, allowHTML: true })
        this.$router.push({ name: 'dialog', params: { dialogId } })
        this.$emit('dialog-change')
      } catch (error) {
        const message = error.messages
          ? error.messages.map(el => this.$t(el)).join(' ')
          : error
        this.$errorMessage(message)
        this.saveStatus = oldStatus
        return
      }
    },

    onEditingCancel() {
      this.onEdited.cancel()
      this.$router.push({ name: 'dialogs' })
    },

    async onDeleteDraft() {
      this.onEdited.cancel()
      await Dialog.deleteDialogDraft(this.userId, this.editMessage)
      this.$successMessage(this.$t('messages.dialog.deleteSuccess'))
      this.$router.push({ name: 'dialogs' })
    }
  }
}
</script>
